import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { THEMES, getTheme, setTheme, MODES, getMode, setMode } from '../theme';
import { useAuth } from '../auth';
import AspettoPicker from '../components/AspettoPicker';
import UtentiCard from '../components/UtentiCard';
import SsmateUtentiCard from '../components/SsmateUtentiCard';
import SsmateCodaCard from '../components/SsmateCodaCard';
import AccessiPubbliciCard from '../components/AccessiPubbliciCard';
import AgentiSalesmateCard from '../components/AgentiSalesmateCard';
import CronologiaAccessiCard from '../components/CronologiaAccessiCard';
import LineaSpegniDialog from '../components/LineaSpegniDialog';
import { IconSettings, IconUsers, IconBot, IconMail, IconTools } from '../components/Icons';
import './impostazioni.css';

/* ── Reusable Field component (controlled, no inline save) ── */
function Field({ label, fieldName, type = 'text', min, suffix, placeholder, value, onChange, options }) {
  const inputStyle = {
    padding: '7px 10px',
    border: '1px solid var(--line)',
    borderRadius: 8,
    fontSize: 13,
    background: 'var(--surface-2)',
    color: 'var(--text)',
    width: type === 'number' ? 100 : '100%',
    maxWidth: (type === 'text' || type === 'password') && !suffix ? 360 : undefined,
  };

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 0',
    borderBottom: '1px solid var(--line)',
    flexWrap: 'wrap',
  };

  const labelStyle = {
    flex: '1 1 170px',
    minWidth: 130,
    fontSize: 13,
    color: 'var(--muted)',
    fontWeight: 500,
  };

  return (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      {type === 'select' ? (
        <select
          value={value ?? ''}
          onChange={e => onChange(fieldName, e.target.value)}
          style={inputStyle}
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          min={min}
          placeholder={placeholder}
          value={value ?? ''}
          onChange={e => onChange(fieldName, type === 'number' ? Number(e.target.value) : e.target.value)}
          style={inputStyle}
        />
      )}
      {suffix && <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{suffix}</span>}
    </div>
  );
}

/* ── Per-card save bar ── */
function SaveBar({ dirty, saving, onSave }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 14, paddingTop: 14, marginTop: 2 }}>
      {dirty && (
        <span style={{ fontSize: 12, color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--warn)', display: 'inline-block' }} />
          modifiche non salvate
        </span>
      )}
      <button
        className="btn btn-primary"
        disabled={!dirty || saving}
        onClick={onSave}
        style={{ minWidth: 120, opacity: dirty ? 1 : 0.5, cursor: dirty && !saving ? 'pointer' : 'default' }}
      >
        {saving ? <span className="spin">&#9696;</span> : null}
        {saving ? ' Salvataggio...' : ' Salva'}
      </button>
    </div>
  );
}

/* ── Health log table ── */
function HealthLogTable({ items }) {
  if (!items || items.length === 0) return null;

  const fmt = ts => {
    try { return new Date(ts).toLocaleString('it-IT'); } catch { return ts; }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
        Health Log
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              {['Data', 'Tipo', 'Errori', 'Durata'].map(h => (
                <th key={h} style={{
                  textAlign: 'left', padding: '8px 6px',
                  fontSize: 11, fontWeight: 700, color: 'var(--muted)',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  borderBottom: '2px solid var(--line)', background: 'var(--surface-2)',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--surface-2)', color: 'var(--muted)' }}>{fmt(it.timestamp)}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--surface-2)' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    background: it.tipo === 'ok' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                    color: it.tipo === 'ok' ? 'var(--ok)' : 'var(--err)',
                  }}>{it.tipo}</span>
                </td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--surface-2)', color: 'var(--muted)' }}>
                  {it.errori_consecutivi_al_momento ?? '-'}
                </td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--surface-2)', color: 'var(--muted)' }}>
                  {it.durata_minuti != null ? `${it.durata_minuti} min` : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const eq = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/* ── Main page ── */
export default function ImpostazioniPage() {
  const [settings, setSettings] = useState(null);  // server state
  const [draft, setDraft] = useState(null);        // working copy (edited locally)
  const [savingKey, setSavingKey] = useState(null);
  const [log, setLog] = useState([]);
  const [testing, setTesting] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [reportSending, setReportSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testingLine, setTestingLine] = useState(null);   // indice linea in test, o null
  const [lineTests, setLineTests] = useState({});          // { [indice]: {success, message, accounts} }
  const [spegni, setSpegni] = useState(null);              // linea da disattivare, in attesa di conferma
  const [theme, setThemeState] = useState(getTheme());
  const [mode, setModeState] = useState(getMode());
  const [license, setLicense] = useState(null);
  const [checkinBusy, setCheckinBusy] = useState(false);
  const { user, can } = useAuth();

  const chooseTheme = (id) => { setTheme(id); setThemeState(id); };
  const chooseMode = (id) => { setMode(id); setModeState(id); };

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      const data = res.data;
      // Normalize: build sip_servers from legacy single-server config if missing
      let servers = data.sip_servers || [];
      if (!servers || servers.length === 0) {
        let accs = data.sip_accounts || [];
        if ((!accs || accs.length === 0) && data.sip_username) {
          accs = [{ username: data.sip_username, password: data.sip_password || '' }];
        }
        if (data.sip_server || accs.length) {
          servers = [{
            nome: 'Linea 1', server: data.sip_server || '', dominio: data.sip_domain || '',
            trasporto: (data.sip_transport || 'udp'), porta: data.sip_port != null ? data.sip_port : 5060,
            accounts: accs,
          }];
        }
      }
      const normalized = { ...data, sip_servers: servers };
      setSettings(normalized);
      setDraft(normalized);
    } catch (err) {
      toast.error('Errore caricamento impostazioni');
    }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/health-log`);
      setLog(res.data.items || []);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadLog();
    axios.get(`${API}/license/status`).then(r => setLicense(r.data)).catch(() => {});
  }, [loadSettings, loadLog]);

  const adminVisible = user?.ruolo === 'admin';
  // Campi Notifiche salvabili: i server SMTP sono admin-only (vedi PROTECTED_KEYS lato backend);
  // all'operatore restano toggle notifiche, destinatari e scelte notifica_*.
  const NOTIF_COMMON = ['notifiche_abilitate', 'smtp_to', 'notifica_standby', 'notifica_target'];
  const NOTIF_SMTP = ['smtp_host', 'smtp_port', 'smtp_user', 'smtp_password', 'smtp_from', 'smtp_tls'];
  const notifFields = adminVisible ? [...NOTIF_COMMON, ...NOTIF_SMTP] : NOTIF_COMMON;
  const [sez, setSez] = useState('generale');   // sezione attiva (tab laterale)
  const [q, setQ] = useState('');               // ricerca impostazioni

  const copyMachineId = () => {
    if (!license?.machine_id) return;
    try { navigator.clipboard.writeText(license.machine_id); toast.success('ID macchina copiato'); }
    catch { toast.error('Copia non riuscita'); }
  };

  const richiediLicenza = async () => {
    setCheckinBusy(true);
    try {
      const r = await axios.post(`${API}/license/checkin-now`);
      if (r.data?.ok) toast.success(`Check-in: ${r.data.stato}`);
      else toast.error(r.data?.reason || 'Errore check-in');
      if (r.data?.status) setLicense(r.data.status);
    } catch { toast.error('Errore check-in'); }
    setCheckinBusy(false);
  };

  // ── Draft editing ──
  const setField = useCallback((name, value) => {
    setDraft(d => ({ ...d, [name]: value }));
  }, []);

  const cardDirty = useCallback((fields) => {
    if (!draft || !settings) return false;
    return fields.some(f => !eq(draft[f], settings[f]));
  }, [draft, settings]);

  const saveCard = useCallback(async (key, fields) => {
    const payload = {};
    fields.forEach(f => { if (!eq(draft[f], settings[f])) payload[f] = draft[f]; });
    if (Object.keys(payload).length === 0) return;
    setSavingKey(key);
    try {
      await axios.put(`${API}/settings`, payload);
      setSettings(prev => ({ ...prev, ...payload }));
      toast.success('Salvato');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore salvataggio';
      toast.error(msg);
      loadSettings(); // reset draft+settings to server state
    } finally {
      setSavingKey(null);
    }
  }, [draft, settings, loadSettings]);

  // ── SIP servers/lines management (draft only) ──
  const sipServers = draft?.sip_servers || [];
  // Editing the config invalidates the last test result (dots would be stale/misleading)
  const mutateSip = (fn) => { setLineTests({}); setDraft(fn); };
  const addLinea = () => mutateSip(d => ({ ...d, sip_servers: [...(d.sip_servers || []),
    { nome: '', tipo: 'fritz', server: '', dominio: '', trasporto: 'udp', porta: 5060, accounts: [] }] }));
  const removeLinea = (i) => mutateSip(d => ({ ...d, sip_servers: (d.sip_servers || []).filter((_, idx) => idx !== i) }));

  // ── Interruttore della linea ──
  // A differenza degli altri campi della scheda, l'interruttore SALVA SUBITO: e' un comando,
  // non una modifica da rivedere, e deve fare effetto sul lavoro in corso.
  const applicaAttivo = async (i, attivo) => {
    const nuove = (draft?.sip_servers || []).map((s, idx) => idx === i ? { ...s, attivo } : s);
    setLineTests({});
    setDraft(d => ({ ...d, sip_servers: nuove }));
    try {
      await axios.put(`${API}/settings`, { sip_servers: nuove });
      setSettings(prev => ({ ...prev, sip_servers: nuove }));
      toast.success(attivo ? 'Linea riattivata' : 'Linea disattivata');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
      loadSettings();
    }
  };
  const toggleLinea = async (i) => {
    const linea = sipServers[i] || {};
    if (linea.attivo === false) { applicaAttivo(i, true); return; }   // riaccendere non ha controindicazioni
    try {
      const r = await axios.get(`${API}/linee/stato`);
      const st = (r.data?.linee || []).find(l => l.indice === i);
      if (st?.numero_corrente) { setSpegni({ i, nome: st.nome, numero_corrente: st.numero_corrente }); return; }
    } catch { /* stato non leggibile: si spegne lo stesso, il worker chiude a fine numero */ }
    applicaAttivo(i, false);
  };
  const updateLinea = (i, field, value) => mutateSip(d => ({ ...d,
    sip_servers: (d.sip_servers || []).map((s, idx) => idx === i ? { ...s, [field]: value } : s) }));
  const addInterno = (i) => mutateSip(d => ({ ...d,
    sip_servers: (d.sip_servers || []).map((s, idx) => idx === i
      ? { ...s, accounts: [...(s.accounts || []), { username: '', password: '' }] } : s) }));
  const removeInterno = (i, j) => mutateSip(d => ({ ...d,
    sip_servers: (d.sip_servers || []).map((s, idx) => idx === i
      ? { ...s, accounts: (s.accounts || []).filter((_, jdx) => jdx !== j) } : s) }));
  const updateInterno = (i, j, field, value) => mutateSip(d => ({ ...d,
    sip_servers: (d.sip_servers || []).map((s, idx) => idx === i
      ? { ...s, accounts: (s.accounts || []).map((a, jdx) => jdx === j ? { ...a, [field]: value } : a) } : s) }));

  // Per-account registration status from the last per-line SIP tests (keyed by server||username)
  const sipStatus = {};
  Object.values(lineTests).forEach(res => {
    (res?.accounts || []).forEach(a => { sipStatus[`${a.server}||${a.username}`] = a.registered; });
  });

  const handleTestEmail = async () => {
    setEmailTesting(true);
    try {
      await axios.post(`${API}/settings/test-email`);
      toast.success('Email di prova inviata');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invio email fallito');
    }
    setEmailTesting(false);
  };

  const handleReportNow = async () => {
    setReportSending(true);
    try {
      await axios.post(`${API}/settings/report-invia`);
      toast.success('Report inviato');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invio report fallito');
    }
    setReportSending(false);
  };

  const handleTestHealth = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/settings/test-health`);
      setTestResult(res.data);
      if (res.data.operativo) {
        toast.success(`456 operativo — esito ${res.data.risultato}`);
      } else {
        toast.error(`456 in disservizio — nessun esito valido (${res.data.risultato})`);
      }
      loadLog();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore test health';
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const testLinea = async (i) => {
    setTestingLine(i);
    try {
      const r = await axios.post(`${API}/settings/test-sip`, { linea: i });
      setLineTests(prev => ({ ...prev, [i]: r.data }));
      if (r.data.success) toast.success(`Linea ${i + 1}: ${r.data.message}`);
      else toast.error(r.data.message || 'Test SIP fallito');
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.[0]?.msg || 'Errore test SIP');
      setLineTests(prev => ({ ...prev, [i]: { success: false, message: msg, accounts: [] } }));
      toast.error(msg);
    }
    setTestingLine(null);
  };

  const fieldProps = (fieldName, type = 'text', extra = {}) => ({
    fieldName,
    type,
    value: draft?.[fieldName],
    onChange: setField,
    ...extra,
  });

  if (!settings || !draft) {
    return (
      <div className="imp-page">
        <h1 style={{ fontSize: 20, marginBottom: 16 }}>Impostazioni</h1>
        <div className="imp-card">
          <span className="spin" style={{ fontSize: 20 }}>&#9696;</span>
          <span style={{ marginLeft: 10, color: 'var(--muted)' }}>Caricamento...</span>
        </div>
      </div>
    );
  }

  const sipFields = ['use_builtin_sip', 'sip_servers'];
  const sipDirty = cardDirty(sipFields);

  const sezDefs = [
    { id: 'generale', label: 'Generale', icon: <IconSettings size={16} />, show: can('imp_tema') || can('imp_licenza') },
    { id: 'utenti', label: 'Utenti & Accessi', icon: <IconUsers size={16} />, show: adminVisible || can('imp_accessi_pubblici') },
    { id: 'agenti', label: 'Agenti', icon: <IconBot size={16} />, show: adminVisible || can('imp_copertura') || can('imp_456') || can('imp_vf') || can('imp_disservizio') || can('imp_accessi_pubblici') },
    { id: 'notifiche', label: 'Notifiche', icon: <IconMail size={16} />, show: adminVisible || can('imp_notifiche') || can('imp_report') || can('imp_export') },
    { id: 'sistema', label: 'Sistema', icon: <IconTools size={16} />, show: adminVisible },
  ].filter(s => s.show);
  const curSez = sezDefs.find(s => s.id === sez) ? sez : (sezDefs[0]?.id);
  const visible = (secId, kw) => {
    const term = q.trim().toLowerCase();
    if (term) return `${kw} ${secId}`.toLowerCase().includes(term);
    return curSez === secId;
  };

  return (
    <div className="imp-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 20, margin: 0 }}>Impostazioni</h1>
        {user && (
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>
            {user.username} · {user.ruolo === 'admin' ? 'Amministratore' : 'Operatore'}
          </span>
        )}
      </div>
      <input className="set-search" placeholder="Cerca un'impostazione…" value={q} onChange={e => setQ(e.target.value)} />
      <div className="set-wrap">
        <aside className="set-rail">
          {sezDefs.map(s => (
            <button key={s.id} type="button"
              className={`set-tab${(!q && curSez === s.id) ? ' on' : ''}`}
              onClick={() => { setSez(s.id); setQ(''); }}>
              <span className="ic">{s.icon}</span> {s.label}
            </button>
          ))}
        </aside>
        <div className="set-main">
      {adminVisible && visible('utenti', 'utenti ruoli permessi password') && <UtentiCard />}
      {adminVisible && visible('agenti', 'ssmate credenziali utenti tim digital sales mate') && <SsmateUtentiCard />}
      {adminVisible && visible('agenti', 'ssmate coda quota crediti') && <SsmateCodaCard />}
      {(adminVisible || can('imp_accessi_pubblici')) && visible('utenti', 'accessi pubblici link registrazione') && <AccessiPubbliciCard />}
      {(adminVisible || can('imp_accessi_pubblici')) && visible('agenti', 'agenti salesmate account registrazione utenti') && <AgentiSalesmateCard />}
      {adminVisible && visible('utenti', 'cronologia accessi login ip device storico') && <CronologiaAccessiCard />}

      {/* Card: Licenza */}
      {can('imp_licenza') && visible('generale', 'licenza chiave attivazione macchina') && (
      <div className="imp-card">
        <h2 className="imp-card-title">Licenza</h2>
        {!license ? (
          <span style={{ color: 'var(--muted)', fontSize: 13 }}>Caricamento…</span>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--surface-2)' }}>
              <span style={{ flex: '0 0 260px', fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>Stato</span>
              <span style={{
                fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
                background: license.valid ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                color: license.valid ? 'var(--ok)' : 'var(--err)',
              }}>
                {license.valid ? 'Valida' : `Non valida — ${license.reason}`}
              </span>
            </div>
            {license.cliente && (
              <div style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--surface-2)' }}>
                <span style={{ flex: '0 0 260px', fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>Cliente</span>
                <span style={{ fontSize: 13 }}>{license.cliente}</span>
              </div>
            )}
            {license.scadenza && (
              <div style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--surface-2)' }}>
                <span style={{ flex: '0 0 260px', fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>Scadenza</span>
                <span style={{ fontSize: 13 }}>
                  {license.scadenza}
                  {license.giorni_rimasti != null && (
                    <span style={{ color: license.giorni_rimasti <= 30 ? 'var(--warn)' : 'var(--muted)', marginLeft: 8 }}>
                      ({license.giorni_rimasti} giorni)
                    </span>
                  )}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', flexWrap: 'wrap' }}>
              <span style={{ flex: '0 0 260px', fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>ID macchina</span>
              <code style={{ fontSize: 12, background: 'var(--surface-2)', padding: '4px 8px', borderRadius: 6, wordBreak: 'break-all' }}>
                {license.machine_id}
              </code>
              <button className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }} onClick={copyMachineId}>Copia</button>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--faint)' }}>
              Comunica l'ID macchina al fornitore per ricevere o rinnovare la licenza.
            </p>
          </>
        )}

        {adminVisible && (
        <div style={{ borderTop: '1px solid var(--surface-2)', marginTop: 10, paddingTop: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            Attivazione automatica (server licenze)
          </div>
          <Field label="URL server licenze" {...fieldProps('license_server_url', 'text')} placeholder="https://licenze.tuodominio.it" />
          <Field label="Nome cliente" {...fieldProps('license_cliente', 'text')} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={richiediLicenza} disabled={checkinBusy} style={{ minWidth: 200 }}>
              {checkinBusy ? 'Verifica…' : 'Richiedi / Rinnova licenza'}
            </button>
            <span style={{ fontSize: 12, color: 'var(--faint)' }}>Invia la richiesta al server (usa l'URL salvato) e attiva/aggiorna la licenza.</span>
          </div>
          <SaveBar
            dirty={cardDirty(['license_server_url', 'license_cliente'])}
            saving={savingKey === 'licenza_cfg'}
            onSave={() => saveCard('licenza_cfg', ['license_server_url', 'license_cliente'])}
          />
        </div>
        )}
      </div>
      )}

      {/* Card 0: Tema colore (locale, nessun salvataggio server) */}
      {can('imp_tema') && visible('generale', 'tema colore aspetto') && (
      <div className="imp-card">
        <h2 className="imp-card-title">Aspetto e colore</h2>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--muted)' }}>
          Modo chiaro/scuro e colore accento dell'interfaccia.
        </p>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Modo</div>
        <div style={{ display: 'inline-flex', gap: 4, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 12, padding: 4, marginBottom: 18 }}>
          {MODES.map(m => {
            const on = mode === m.id;
            return (
              <button key={m.id} type="button" onClick={() => chooseMode(m.id)}
                style={{ border: 'none', cursor: 'pointer', borderRadius: 9, padding: '7px 16px', fontSize: 13, fontWeight: 700,
                  background: on ? 'var(--accent)' : 'transparent', color: on ? 'var(--on-accent)' : 'var(--muted)' }}>
                {m.label}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Colore accento</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {THEMES.map(t => {
            const active = theme === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => chooseTheme(t.id)}
                title={t.label}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  padding: '8px 6px', cursor: 'pointer', width: 70,
                  borderRadius: 12,
                  border: active ? '2px solid var(--accent)' : '2px solid var(--line)',
                  background: active ? 'var(--accent-soft)' : 'var(--surface-2)',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: t.swatch,
                  boxShadow: active ? '0 0 0 2px var(--surface), 0 0 8px 1px ' + t.swatch : '0 2px 6px rgba(0,0,0,0.25)',
                  border: '2px solid var(--surface)',
                }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: active ? 'var(--accent)' : 'var(--muted)', whiteSpace: 'nowrap' }}>
                  {active ? `✓ ${t.label}` : t.label}
                </span>
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            Aspetto (stile delle linee)
          </div>
          <AspettoPicker />
        </div>
      </div>
      )}

      {(adminVisible || can('imp_456')) && visible('agenti', 'verifica 456 timing ri-verifica varianti gnr pausa') && (<>
      {/* Card 1: Verifica 456 */}
      <div className="imp-card">
        <h2 className="imp-card-title">Verifica 456</h2>
        <Field
          label="Timing ri-verifica"
          {...fieldProps('timing_riverifica_giorni', 'number')}
          min={180}
          suffix="giorni (min 180)"
        />
        <Field
          label="Pausa tra varianti GNR"
          {...fieldProps('pausa_varianti_secondi', 'number')}
          suffix="secondi"
        />
        <Field
          label="Pausa retry servizio"
          {...fieldProps('pausa_retry_secondi', 'number')}
          suffix="secondi"
        />
        <Field
          label="Attesa agenti (ricontrollo quando senza record)"
          {...fieldProps('agenti_attesa_minuti', 'number')}
          suffix="minuti"
        />
        <SaveBar
          dirty={cardDirty(['timing_riverifica_giorni', 'pausa_varianti_secondi', 'pausa_retry_secondi', 'agenti_attesa_minuti'])}
          saving={savingKey === 'verifica'}
          onSave={() => saveCard('verifica', ['timing_riverifica_giorni', 'pausa_varianti_secondi', 'pausa_retry_secondi', 'agenti_attesa_minuti'])}
        />
      </div>
      </>)}

      {/* Card: Copertura TIM (bulk + agente) */}
      {(adminVisible || can('imp_copertura')) && visible('agenti', 'copertura tim ftth fttc fwa adsl timing concorrenza notifica') && (
      <div className="imp-card">
        <h2 className="imp-card-title">Copertura TIM</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <label style={{ flex: 1 }}>Verifica copertura</label>
          <select
            value={(draft?.copertura_abilitata ?? true) ? 'true' : 'false'}
            onChange={e => setField('copertura_abilitata', e.target.value === 'true')}
          >
            <option value="true">Abilitata</option>
            <option value="false">Disabilitata (blocca bulk e agente)</option>
          </select>
        </div>
        <Field label="Timing FTTH (già fibra)" {...fieldProps('copertura_timing_ftth', 'number')} suffix="giorni" />
        <Field label="Timing monitoraggio (FTTC/FWA/ADSL)" {...fieldProps('copertura_timing_monitor', 'number')} suffix="giorni" />
        <Field label="Timing default (non coperto / altro)" {...fieldProps('copertura_timing_giorni', 'number')} suffix="giorni" />
        <div style={{ fontSize: 11, color: 'var(--faint)', margin: '-6px 0 8px' }}>
          FTTC/FWA/ADSL sono i futuri FTTH: timing breve per intercettare il passaggio a fibra. Quando un numero diventa FTTH compare il badge <b>NEW</b> e (se attiva) parte la notifica email.
        </div>
        <div className="f-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '2px 0 8px' }}>
          <label style={{ fontSize: 13 }}>Notifica email passaggio a FTTH</label>
          <select value={(draft?.notifica_ftth ?? true) ? 'true' : 'false'} onChange={e => setField('notifica_ftth', e.target.value === 'true')}>
            <option value="true">Attiva</option><option value="false">Disattiva</option>
          </select>
        </div>
        <Field label="Pausa tra verifiche" {...fieldProps('copertura_pausa_secondi', 'number')} suffix="secondi" />
        <Field label="Verifiche in parallelo (agente)" {...fieldProps('copertura_concorrenza', 'number')} suffix="browser (1–5)" />
        <div style={{ fontSize: 11, color: 'var(--faint)', margin: '-6px 0 8px' }}>
          Più verifiche contemporanee = più veloce, ma alza il rischio di blocco/CAPTCHA da TIM. Consigliato 2–3.
        </div>
        <Field label="Quota record mai verificati (agente)" {...fieldProps('copertura_quota_nuovi', 'number')} suffix="% del lotto" />
        <div style={{ fontSize: 11, color: 'var(--faint)', margin: '-6px 0 8px' }}>
          Come l'agente divide il lotto fra <b>record nuovi</b> (mai verificati) e <b>scadenze</b> del timing: senza una quota, nei giorni di ondata le scadenze servono prima i record nuovi, che finiscono in coda. È un <b>minimo garantito, non un tetto</b>: se una delle due parti ha meno lavoro della sua fetta, l'altra si prende tutti i posti. <b>0</b> = solo scadenze, <b>100</b> = solo record nuovi.
        </div>
        <Field label="Soglia errori → standby" {...fieldProps('copertura_soglia_errori', 'number')} suffix="errori consecutivi" />
        <Field label="Durata standby" {...fieldProps('copertura_standby_minuti', 'number')} suffix="minuti" />
        <SaveBar
          dirty={cardDirty(['copertura_abilitata', 'copertura_timing_giorni', 'copertura_timing_ftth', 'copertura_timing_monitor', 'notifica_ftth', 'copertura_pausa_secondi', 'copertura_concorrenza', 'copertura_quota_nuovi', 'copertura_soglia_errori', 'copertura_standby_minuti'])}
          saving={savingKey === 'copertura'}
          onSave={() => saveCard('copertura', ['copertura_abilitata', 'copertura_timing_giorni', 'copertura_timing_ftth', 'copertura_timing_monitor', 'notifica_ftth', 'copertura_pausa_secondi', 'copertura_concorrenza', 'copertura_quota_nuovi', 'copertura_soglia_errori', 'copertura_standby_minuti'])}
        />
      </div>
      )}

      {/* Card: AI VF (Vodafone) */}
      {(adminVisible || can('imp_vf')) && visible('agenti', 'vf vodafone gea linea timing concorrenza') && (
      <div className="imp-card">
        <h2 className="imp-card-title">AI VF · linea Vodafone</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <label style={{ flex: 1 }}>AI VF (Vodafone)</label>
          <select value={(draft?.gea_abilitata ?? true) ? 'true' : 'false'} onChange={e => setField('gea_abilitata', e.target.value === 'true')}>
            <option value="true">Abilitata</option>
            <option value="false">Disabilitata (blocca agente e singola)</option>
          </select>
        </div>
        <Field label="Timing ri-verifica" {...fieldProps('gea_timing_giorni', 'number')} suffix="giorni" />
        {/* Il ritmo: e' quello che tiene lontano un altro blocco del portale (18/09/2026,
            quando Vodafone ci ha bloccati dopo 3.668 verifiche in una giornata). Gli stessi
            valori si regolano anche dalla finestra della spia VF. */}
        <Field label="Pausa fra una richiesta e l’altra" {...fieldProps('gea_pausa_secondi', 'number')} suffix="secondi (2–120)" />
        <Field label="Tetto giornaliero" {...fieldProps('gea_max_giorno', 'number')} suffix="verifiche al giorno" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0' }}>
          <label style={{ flex: 1 }}>Su chi lavora</label>
          <select value={(draft?.gea_solo_pool ?? true) ? 'true' : 'false'}
                  onChange={e => setField('gea_solo_pool', e.target.value === 'true')}>
            <option value="true">Solo i clienti pronti da lavorare (consigliato)</option>
            <option value="false">Tutto l’archivio NON TIM</option>
          </select>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--faint)', margin: '2px 0 8px' }}>
          Le <b>fasce orarie</b> (una per giorno della settimana) si impostano dal cruscotto agenti:
          icona dei cursori → <b>Gestione agenti</b> → ingranaggio accanto a «Priorità» su AI VF.
        </div>
        <div style={{ fontSize: 11, color: 'var(--faint)', margin: '-6px 0 8px' }}>
          Gira solo sui numeri <b>NON TIM</b> (fissi), <b>una verifica alla volta</b>. La pausa viene
          variata di un 30% a caso, e ogni 100 verifiche l’agente prova una linea che sappiamo
          essere Vodafone: se risponde «negativo» vuol dire che il portale ci sta bloccando, e
          l’agente si ferma da solo. Gli utenti col permesso <b>AI VF</b> vedranno il <b>logo
          Vodafone</b> accanto al risultato 456.
        </div>
        <SaveBar
          dirty={cardDirty(['gea_abilitata', 'gea_timing_giorni', 'gea_pausa_secondi',
                            'gea_max_giorno', 'gea_solo_pool'])}
          saving={savingKey === 'gea'}
          onSave={() => saveCard('gea', ['gea_abilitata', 'gea_timing_giorni', 'gea_pausa_secondi',
                                         'gea_max_giorno', 'gea_solo_pool'])}
        />
      </div>
      )}

      {/* Card: Agente Indaga (completa indirizzi) */}
      {adminVisible && (
      <div className="imp-card">
        <h2 className="imp-card-title">Agente Indaga · completa indirizzi</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <label style={{ flex: 1 }}>Agente Indaga</label>
          <select value={(draft?.indaga_abilitata ?? true) ? 'true' : 'false'} onChange={e => setField('indaga_abilitata', e.target.value === 'true')}>
            <option value="true">Abilitato</option>
            <option value="false">Disabilitato (blocca l'agente)</option>
          </select>
        </div>
        <Field label="Cooldown ri-tentativo" {...fieldProps('indaga_timing_giorni', 'number')} suffix="giorni" />
        <Field label="Pausa tra ricerche" {...fieldProps('indaga_pausa_secondi', 'number')} suffix="secondi" />
        <Field label="Tetto giornaliero" {...fieldProps('indaga_max_giorno', 'number')} suffix="ricerche/giorno" />
        <Field label="Standby dopo errori consecutivi" {...fieldProps('indaga_soglia_errori', 'number')} suffix="errori" />
        <Field label="Durata standby" {...fieldProps('indaga_standby_minuti', 'number')} suffix="minuti" />
        <div style={{ fontSize: 11, color: 'var(--faint)', margin: '-6px 0 8px' }}>
          Completa gli indirizzi incompleti solo con <b>match P.IVA</b> (certezza 100%). Conservativo verso Google: una ricerca per volta.
        </div>
        <SaveBar
          dirty={cardDirty(['indaga_abilitata', 'indaga_timing_giorni', 'indaga_pausa_secondi', 'indaga_max_giorno', 'indaga_soglia_errori', 'indaga_standby_minuti'])}
          saving={savingKey === 'indaga'}
          onSave={() => saveCard('indaga', ['indaga_abilitata', 'indaga_timing_giorni', 'indaga_pausa_secondi', 'indaga_max_giorno', 'indaga_soglia_errori', 'indaga_standby_minuti'])}
        />
      </div>
      )}

      {/* Card: Notifiche email (SMTP) */}
      {(adminVisible || can('imp_notifiche')) && visible('notifiche', 'notifiche email smtp server') && (
      <div className="imp-card">
        <h2 className="imp-card-title">Notifiche email (SMTP)</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <label style={{ flex: 1 }}>Notifiche email</label>
          <select value={(draft?.notifiche_abilitate ?? false) ? 'true' : 'false'}
            onChange={e => setField('notifiche_abilitate', e.target.value === 'true')}>
            <option value="true">Attive</option>
            <option value="false">Disattivate</option>
          </select>
        </div>
        {adminVisible ? (
          <>
            <Field label="Server SMTP (host)" {...fieldProps('smtp_host', 'text')} />
            <Field label="Porta" {...fieldProps('smtp_port', 'number')} suffix="587 (TLS) o 465 (SSL)" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--surface-2)' }}>
              <label style={{ flex: 1 }}>STARTTLS</label>
              <select value={(draft?.smtp_tls ?? true) ? 'true' : 'false'} onChange={e => setField('smtp_tls', e.target.value === 'true')}>
                <option value="true">Sì (porta 587)</option>
                <option value="false">No (porta 465/SSL)</option>
              </select>
            </div>
            <Field label="Utente" {...fieldProps('smtp_user', 'text')} />
            <Field label="Password" {...fieldProps('smtp_password', 'password')} />
            <Field label="Mittente (From)" {...fieldProps('smtp_from', 'text')} />
          </>
        ) : (
          <p style={{ fontSize: 12, color: 'var(--muted)', padding: '10px 0', borderBottom: '1px solid var(--surface-2)', margin: 0 }}>
            Il server SMTP è configurato dall'amministratore. Qui puoi gestire destinatari e avvisi.
          </p>
        )}
        <Field label="Destinatari (To)" {...fieldProps('smtp_to', 'text')} suffix="separati da virgola" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <label style={{ flex: 1 }}>Avvisa su standby (blocco SIP/disservizio)</label>
          <select value={(draft?.notifica_standby ?? true) ? 'true' : 'false'} onChange={e => setField('notifica_standby', e.target.value === 'true')}>
            <option value="true">Sì</option><option value="false">No</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <label style={{ flex: 1 }}>Avvisa su obiettivo raggiunto</label>
          <select value={(draft?.notifica_target ?? true) ? 'true' : 'false'} onChange={e => setField('notifica_target', e.target.value === 'true')}>
            <option value="true">Sì</option><option value="false">No</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0' }}>
          <button className="btn btn-secondary" onClick={handleTestEmail} disabled={emailTesting}>
            {emailTesting ? 'Invio…' : 'Invia email di prova'}
          </button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Salva prima la configurazione, poi prova.</span>
        </div>
        <SaveBar
          dirty={cardDirty(notifFields)}
          saving={savingKey === 'notifiche'}
          onSave={() => saveCard('notifiche', notifFields)}
        />
      </div>
      )}

      {/* Card: Report automatico */}
      {(adminVisible || can('imp_report')) && visible('notifiche', 'report automatico email pianificato') && (
      <div className="imp-card">
        <h2 className="imp-card-title">Report automatico</h2>
        <div style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 8px' }}>
          Riepilogo periodico via email (usa la connessione SMTP qui sopra).
        </div>
        <Field label="Destinatari report" {...fieldProps('report_to', 'text')} suffix="separati da virgola (vuoto = come SMTP)" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <label style={{ flex: 1 }}>Report automatico</label>
          <select value={(draft?.report_abilitato ?? false) ? 'true' : 'false'}
            onChange={e => setField('report_abilitato', e.target.value === 'true')}>
            <option value="true">Attivo</option>
            <option value="false">Disattivato</option>
          </select>
        </div>
        <Field label="Cadenza" {...fieldProps('report_cadenza', 'select')}
          options={[{ value: 'giornaliero', label: 'Giornaliero' }, { value: 'settimanale', label: 'Settimanale' }]} />
        <Field label="Ora invio" {...fieldProps('report_ora', 'number')} min={0} max={23} suffix="0–23 (orario locale)" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <label style={{ flex: 1 }}>Giorno (se settimanale)</label>
          <select value={String(draft?.report_giorno ?? 0)} onChange={e => setField('report_giorno', Number(e.target.value))}>
            {['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'].map((g, i) =>
              <option key={i} value={i}>{g}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0' }}>
          <button className="btn btn-secondary" onClick={handleReportNow} disabled={reportSending}>
            {reportSending ? 'Invio…' : 'Invia report ora'}
          </button>
        </div>
        <SaveBar
          dirty={cardDirty(['report_abilitato', 'report_cadenza', 'report_ora', 'report_giorno', 'report_to'])}
          saving={savingKey === 'report'}
          onSave={() => saveCard('report', ['report_abilitato', 'report_cadenza', 'report_ora', 'report_giorno', 'report_to'])}
        />
      </div>
      )}

      {(adminVisible || can('imp_disservizio')) && visible('agenti', 'disservizio 456 controllo numero standby salute') && (
      <div className="imp-card">
        <h2 className="imp-card-title">Controllo disservizio 456</h2>
        <Field
          label="Numero di controllo"
          {...fieldProps('numero_controllo', 'text')}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <button
            className="btn btn-primary"
            onClick={handleTestHealth}
            disabled={testing}
            style={{ minWidth: 120 }}
          >
            {testing ? <span className="spin">&#9696;</span> : null}
            {testing ? ' Test in corso...' : ' Testa ora'}
          </button>
          {testResult && (
            <span style={{
              fontSize: 12, padding: '4px 10px', borderRadius: 10, fontWeight: 600,
              background: testResult.operativo ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
              color: testResult.operativo ? 'var(--ok)' : 'var(--err)',
            }}>
              {testResult.operativo
                ? `Operativo — esito ${testResult.risultato}`
                : `Disservizio — nessun esito (${testResult.risultato})`}
            </span>
          )}
        </div>

        <Field
          label="Errori consecutivi prima del test"
          {...fieldProps('soglia_errori_consecutivi', 'number')}
        />
        <Field
          label="Pausa standby se disservizio"
          {...fieldProps('standby_minuti', 'number')}
          suffix="minuti"
        />
        <SaveBar
          dirty={cardDirty(['numero_controllo', 'soglia_errori_consecutivi', 'standby_minuti'])}
          saving={savingKey === 'disservizio'}
          onSave={() => saveCard('disservizio', ['numero_controllo', 'soglia_errori_consecutivi', 'standby_minuti'])}
        />

        <HealthLogTable items={log} />
      </div>
      )}

      {adminVisible && visible('sistema', 'configurazione sistema microsip path registrazioni export cartella') && (
      <div className="imp-card">
        <h2 className="imp-card-title">Configurazione Sistema</h2>
        <Field
          label="Path MicroSIP"
          {...fieldProps('microsip_path', 'text')}
        />
        <Field
          label="Cartella registrazioni"
          {...fieldProps('recordings_path', 'text')}
        />
        <Field
          label="Cartella export"
          {...fieldProps('exports_path', 'text')}
        />
        <SaveBar
          dirty={cardDirty(['microsip_path', 'recordings_path', 'exports_path'])}
          saving={savingKey === 'sistema'}
          onSave={() => saveCard('sistema', ['microsip_path', 'recordings_path', 'exports_path'])}
        />
      </div>

      )}

      {adminVisible && visible('sistema', 'google places chiave aziende vicine mappe api') && (
      <div className="imp-card">
        <h2 className="imp-card-title">Aziende vicine (Google Places)</h2>
        <p style={{ margin: '0 0 12px', color: 'var(--muted)', fontSize: 13 }}>
          Chiave <b>Google Places API (New)</b> per lo strumento "Vicine" della PWA agente.
          Serve un progetto Google Cloud con Places API abilitata e fatturazione attiva.
        </p>
        <Field label="Chiave Google Places" {...fieldProps('google_places_key', 'password')} />
        <Field label="Tetto richieste al mese (0 = illimitato)" {...fieldProps('google_places_max_mese', 'number')}
          suffix="oltre questo numero la ricerca si blocca (protegge il credito gratis)" />
        <Field label="Crediti Vicine / mese per agente (default 20)" {...fieldProps('vicine_crediti_mese_agente', 'number')}
          suffix="ogni agente PWA può fare al massimo N azioni Vicine/mese: 1 credito per ricerca E 1 per apertura dettagli (controllo costi Google API)" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <label style={{ flex: 1 }}>Solo aziende con sito web</label>
          <select value={((draft?.google_places_solo_sito ?? true) ? 'true' : 'false')}
            onChange={e => setField('google_places_solo_sito', e.target.value === 'true')}>
            <option value="true">Sì</option><option value="false">No</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <label style={{ flex: 1 }}>Solo aziende (nome con SRL/SPA)</label>
          <select value={((draft?.google_places_solo_srlspa ?? true) ? 'true' : 'false')}
            onChange={e => setField('google_places_solo_srlspa', e.target.value === 'true')}>
            <option value="true">Sì</option><option value="false">No</option>
          </select>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', padding: '6px 0 2px' }}>
          Usate questo mese: <b>{draft?._places_uso_mese ?? 0}</b> richieste.
          {' '}Google non espone il credito residuo: per sicurezza imposta anche una <b>quota giornaliera</b> e un <b>budget alert</b> nella Cloud Console.
        </div>
        <SaveBar
          dirty={cardDirty(['google_places_key', 'google_places_max_mese', 'vicine_crediti_mese_agente', 'google_places_solo_sito', 'google_places_solo_srlspa'])}
          saving={savingKey === 'places'}
          onSave={() => saveCard('places', ['google_places_key', 'google_places_max_mese', 'vicine_crediti_mese_agente', 'google_places_solo_sito', 'google_places_solo_srlspa'])}
        />
      </div>
      )}

      {adminVisible && visible('sistema', 'sip client headless linee server account voip') && (
      <div className="imp-card">
        <h2 className="imp-card-title">Client SIP (Headless)</h2>

        <div style={{
          background: 'var(--surface-2)',
          border: '1px solid var(--surface-2)',
          borderRadius: 8,
          padding: '10px 14px',
          marginBottom: 12,
          fontSize: 13,
          color: '#1e40af',
          lineHeight: 1.5,
        }}>
          Attiva il client SIP integrato per eliminare la dipendenza da MicroSIP.
          Il sistema potra&apos; funzionare senza sessione desktop attiva.
        </div>

        {/* Modalita' chiamate — boolean select */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <span style={{ flex: '0 0 260px', fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>
            Modalita&apos; chiamate
          </span>
          <select
            value={draft?.use_builtin_sip ? 'true' : 'false'}
            onChange={e => setField('use_builtin_sip', e.target.value === 'true')}
            style={{ padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, background: 'var(--surface)', maxWidth: 360, width: '100%' }}
          >
            <option value="false">MicroSIP (legacy — richiede desktop)</option>
            <option value="true">Client SIP integrato (headless)</option>
          </select>
        </div>

        {/* Lista linee/server SIP */}
        <div style={{ padding: '8px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>
              Linee SIP <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(piu' linee = piu' chiamate in parallelo)</span>
            </span>
            <button onClick={addLinea} className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }}>+ Linea</button>
          </div>

          {sipServers.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 8 }}>Nessuna linea. Aggiungine almeno una.</div>
          )}

          {sipServers.map((linea, i) => {
            const spenta = linea.attivo === false;
            // Assente = FRITZ!Box: e' cosi' che si comportavano tutte prima che
            // questo campo esistesse, e cosi' devono continuare.
            const tipoLinea = linea.tipo || 'fritz';
            const seRegistrazione = tipoLinea !== 'fritz';
            return (
            <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 12, marginBottom: 12, background: 'var(--surface-2)', opacity: spenta ? 0.62 : 1 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                {/* Spenta = configurata ma fuori servizio: non la usa nessuno, né agente né
                    utenti né link. Resta qui per poterla riaccendere. */}
                <button onClick={() => toggleLinea(i)} className="btn btn-secondary"
                  title={spenta ? 'Riattiva la linea' : 'Disattiva la linea (resta configurata)'}
                  style={{ fontSize: 11.5, padding: '6px 10px', fontWeight: 700, whiteSpace: 'nowrap', color: spenta ? 'var(--muted)' : 'var(--ok,#16a34a)' }}>
                  {spenta ? '○ Disattivata' : '● Attiva'}
                </button>
                <input
                  placeholder="Nome linea (es. FRITZ Roma)" value={linea.nome || ''}
                  onChange={e => updateLinea(i, 'nome', e.target.value)}
                  style={{ flex: 1, minWidth: 160, padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, fontWeight: 600 }}
                />
                {/* Che centralino c'e' dall'altra parte. Cambia una cosa sola, ma
                    decisiva: il regno in cui vale la credenziale. Le linee salvate prima
                    che questo campo esistesse non ce l'hanno e valgono FRITZ!Box, cioe'
                    si comportano esattamente come hanno sempre fatto. */}
                <select
                  value={tipoLinea}
                  onChange={e => updateLinea(i, 'tipo', e.target.value)}
                  title="Il tipo di centralino: cambia il modo in cui ci si autentica"
                  style={{ padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, background: 'var(--surface)' }}
                >
                  <option value="fritz">FRITZ!Box</option>
                  <option value="yeastar">Yeastar</option>
                  <option value="altro">Altro centralino</option>
                </select>
                <select
                  value={linea.trasporto || 'udp'}
                  onChange={e => updateLinea(i, 'trasporto', e.target.value)}
                  style={{ padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, background: 'var(--surface)' }}
                >
                  <option value="udp">UDP</option>
                  <option value="tcp">TCP</option>
                </select>
                <input
                  placeholder="Porta" type="number" value={linea.porta ?? 5060}
                  onChange={e => updateLinea(i, 'porta', Number(e.target.value))}
                  style={{ width: 90, padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13 }}
                />
                <button onClick={() => removeLinea(i)} className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 10px' }} title="Rimuovi linea">&#10005;</button>
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <input
                  placeholder="Server (es. roma.myfritz.net)" value={linea.server || ''}
                  onChange={e => updateLinea(i, 'server', e.target.value)}
                  style={{ flex: 2, minWidth: 200, padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13 }}
                />
                <input
                  placeholder="Dominio (vuoto = come server)" value={linea.dominio || ''}
                  onChange={e => updateLinea(i, 'dominio', e.target.value)}
                  style={{ flex: 1, minWidth: 160, padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13 }}
                />
              </div>

              {tipoLinea === 'yeastar' && (
                <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: -4, marginBottom: 10, lineHeight: 1.5 }}>
                  Qui va l&apos;indirizzo del <b>centralino</b>, non quello dell&apos;accesso remoto
                  Yeastar (<code>…ras.yeastar.com</code>): quel servizio pubblica l&apos;interfaccia web
                  e i client Linkus, ma non risponde al SIP. Interno e <i>nome registrazione</i> sono
                  due cose diverse: li trovi nella scheda dell&apos;interno sul centralino.
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>Interni</span>
                <button onClick={() => addInterno(i)} className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }}>+ Interno</button>
              </div>

              {(linea.accounts || []).length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 6 }}>Nessun interno su questa linea.</div>
              )}

              {(linea.accounts || []).map((acc, j) => {
                const reg = sipStatus[`${linea.server}||${acc.username}`];
                const dotColor = reg === true ? 'var(--ok)' : reg === false ? 'var(--err)' : 'var(--line)';
                const dotTitle = reg === true ? 'Registrato' : reg === false ? 'Non registrato' : 'Stato sconosciuto — esegui il test connessione';
                return (
                  <div key={j} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <span title={dotTitle} style={{ width: 11, height: 11, borderRadius: '50%', flex: '0 0 auto', background: dotColor }} />
                    <input
                      placeholder="interno / username" value={acc.username || ''}
                      onChange={e => updateInterno(i, j, 'username', e.target.value)}
                      style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13 }}
                    />
                    {/* Un Yeastar genera un "nome di registrazione" a caso e tiene
                        separata l'identita' (il numero dell'interno) da quel nome. Un
                        FRITZ!Box no: li' il campo non si mostra nemmeno, per non far
                        credere che manchi qualcosa. */}
                    {seRegistrazione && (
                      <input
                        placeholder="nome registrazione" value={acc.auth_username || ''}
                        onChange={e => updateInterno(i, j, 'auth_username', e.target.value)}
                        title="Il nome di registrazione generato dal centralino. Vuoto = si usa l'interno."
                        style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13 }}
                      />
                    )}
                    <input
                      placeholder="password" type="password" value={acc.password || ''}
                      onChange={e => updateInterno(i, j, 'password', e.target.value)}
                      style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13 }}
                    />
                    <button onClick={() => removeInterno(i, j)} className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 10px' }}>&#10005;</button>
                  </div>
                );
              })}

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => testLinea(i)}
                  disabled={testingLine !== null || sipDirty}
                  title={sipDirty ? 'Salva prima di testare' : 'Verifica la registrazione di questa linea'}
                  style={{ fontSize: 12, padding: '6px 14px' }}
                >
                  {testingLine === i ? <span className="spin">&#9696;</span> : null}
                  {testingLine === i ? ' Test in corso...' : 'Testa linea'}
                </button>
                {lineTests[i] && (
                  <span style={{
                    fontSize: 12, padding: '4px 10px', borderRadius: 10, fontWeight: 600,
                    background: lineTests[i].success ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                    color: lineTests[i].success ? 'var(--ok)' : 'var(--err)',
                  }}>
                    {lineTests[i].message}
                  </span>
                )}
                {sipDirty && (
                  <span style={{ fontSize: 11, color: 'var(--warn)' }}>Salva prima di testare</span>
                )}
                {spenta && (
                  <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                    Fuori servizio: non viene usata da nessuno. Il test resta possibile, per provarla prima di riaccenderla.
                  </span>
                )}
              </div>
            </div>
            );
          })}
        </div>

        <SaveBar
          dirty={sipDirty}
          saving={savingKey === 'sip'}
          onSave={() => saveCard('sip', sipFields)}
        />
        <LineaSpegniDialog
          linea={spegni}
          onAnnulla={() => setSpegni(null)}
          onConferma={() => { const i = spegni.i; setSpegni(null); applicaAttivo(i, false); }}
        />
      </div>
      )}

      {/* Card 5: Export Automatico */}
      {can('imp_export') && visible('notifiche', 'export automatico pianificato csv') && (
      <div className="imp-card">
        <h2 className="imp-card-title">Export Automatico</h2>

        <div style={{
          background: 'var(--surface-2)',
          border: '1px solid var(--surface-2)',
          borderRadius: 8,
          padding: '10px 14px',
          marginBottom: 12,
          fontSize: 13,
          color: '#1e40af',
          lineHeight: 1.5,
        }}>
          Il sistema genera automaticamente un lotto di export con tutti i NON TIM disponibili alla cadenza scelta.
          I file appaiono nello storico Export, pronti da scaricare.
        </div>

        {/* Export automatico — boolean select */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <span style={{ flex: '0 0 260px', fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>
            Export automatico
          </span>
          <select
            value={draft?.auto_export_enabled ? 'true' : 'false'}
            onChange={e => setField('auto_export_enabled', e.target.value === 'true')}
            style={{ padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, background: 'var(--surface)', maxWidth: 360, width: '100%' }}
          >
            <option value="false">Disattivato</option>
            <option value="true">Attivo</option>
          </select>
        </div>

        {/* Cadenza select */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <span style={{ flex: '0 0 260px', fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>Cadenza</span>
          <select
            value={draft?.auto_export_cadenza || 'giornaliera'}
            onChange={e => setField('auto_export_cadenza', e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, background: 'var(--surface)', maxWidth: 360, width: '100%' }}
          >
            <option value="giornaliera">Giornaliera</option>
            <option value="settimanale">Settimanale</option>
          </select>
        </div>

        {/* Formato file select */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--surface-2)' }}>
          <span style={{ flex: '0 0 260px', fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>Formato file</span>
          <select
            value={draft?.auto_export_formato || 'unico'}
            onChange={e => setField('auto_export_formato', e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, background: 'var(--surface)', maxWidth: 360, width: '100%' }}
          >
            <option value="unico">File unico</option>
            <option value="regione">Diviso per regione</option>
            <option value="provincia">Diviso per provincia</option>
          </select>
        </div>

        {settings?.auto_export_last && (
          <div style={{ padding: '8px 0', fontSize: 12, color: 'var(--faint)' }}>
            Ultimo export automatico: {new Date(settings.auto_export_last).toLocaleString('it-IT')}
          </div>
        )}

        <SaveBar
          dirty={cardDirty(['auto_export_enabled', 'auto_export_cadenza', 'auto_export_formato'])}
          saving={savingKey === 'export'}
          onSave={() => saveCard('export', ['auto_export_enabled', 'auto_export_cadenza', 'auto_export_formato'])}
        />
      </div>
      )}

      {adminVisible && visible('sistema', 'trascrizione audio whisper modello dispositivo') && (
      <div className="imp-card">
        <h2 className="imp-card-title">Trascrizione Audio</h2>
        <Field
          label="Modello Whisper"
          {...fieldProps('whisper_model', 'select')}
          options={[
            { value: 'tiny', label: 'tiny' },
            { value: 'base', label: 'base' },
            { value: 'small', label: 'small' },
            { value: 'medium', label: 'medium' },
          ]}
        />
        <Field
          label="Dispositivo"
          {...fieldProps('whisper_device', 'select')}
          options={[
            { value: 'cpu', label: 'cpu' },
            { value: 'cuda', label: 'cuda' },
          ]}
        />
        <SaveBar
          dirty={cardDirty(['whisper_model', 'whisper_device'])}
          saving={savingKey === 'whisper'}
          onSave={() => saveCard('whisper', ['whisper_model', 'whisper_device'])}
        />
      </div>
      )}
        </div>
      </div>
    </div>
  );
}
